/**
 * Solitaire Board Component
 *
 * Layout: stock/waste at top-left, 4 foundations at top-right,
 * 7 tableau columns below.
 *
 * Click-based interaction model:
 * - Click face-down tableau card to flip
 * - Click card to select, click target to move
 * - Click stock to draw
 * - Double-click to auto-move to foundation
 * - Click selected card again to deselect
 */

import React, { useCallback } from 'react';
import {
  Card,
  CardSource,
  SolitaireBoardProps,
  SUIT_SYMBOLS,
  RANK_LABELS,
  getCardColor,
} from '../types';
import './SolitaireBoard.css';

const SolitaireBoard: React.FC<SolitaireBoardProps> = ({
  gameState,
  selectedCard,
  onCardClick,
  onEmptyClick,
  onCardDoubleClick,
  onStockClick,
  disabled,
}) => {
  const isSelected = useCallback(
    (card: Card): boolean => {
      return selectedCard?.card.id === card.id;
    },
    [selectedCard]
  );

  const isInSelectedSequence = useCallback(
    (colIndex: number, cardIndex: number): boolean => {
      if (!selectedCard || selectedCard.source.type !== 'tableau') return false;
      return (
        selectedCard.source.index === colIndex &&
        cardIndex >= selectedCard.source.cardIndex
      );
    },
    [selectedCard]
  );

  const renderCard = useCallback(
    (card: Card, source: CardSource, isPartOfSequence: boolean = false) => {
      if (!card.faceUp) {
        return (
          <div
            className="solitaire-card solitaire-card--back"
            onClick={() => !disabled && onCardClick(source, card)}
            role="button"
            aria-label="Face-down card"
            tabIndex={0}
          >
            <div className="solitaire-card__back-pattern" />
          </div>
        );
      }

      const color = getCardColor(card);
      const symbol = SUIT_SYMBOLS[card.suit];
      const rank = RANK_LABELS[card.rank];
      const selected = isSelected(card) || isPartOfSequence;

      return (
        <div
          className={`solitaire-card solitaire-card--${color} ${selected ? 'solitaire-card--selected' : ''}`}
          onClick={() => !disabled && onCardClick(source, card)}
          onDoubleClick={() => !disabled && onCardDoubleClick(source, card)}
          role="button"
          aria-label={`${rank} of ${card.suit}`}
          tabIndex={0}
        >
          <div className="solitaire-card__corner solitaire-card__corner--top">
            <span className="solitaire-card__rank">{rank}</span>
            <span className="solitaire-card__suit">{symbol}</span>
          </div>
          <div className="solitaire-card__center">
            <span className="solitaire-card__center-suit">{symbol}</span>
          </div>
          <div className="solitaire-card__corner solitaire-card__corner--bottom">
            <span className="solitaire-card__rank">{rank}</span>
            <span className="solitaire-card__suit">{symbol}</span>
          </div>
        </div>
      );
    },
    [disabled, isSelected, onCardClick, onCardDoubleClick]
  );

  const renderStock = () => {
    const { stock } = gameState;

    if (stock.length === 0) {
      return (
        <div
          className="solitaire-pile solitaire-pile--stock solitaire-pile--empty"
          onClick={() => !disabled && onStockClick()}
          role="button"
          aria-label="Empty stock pile, click to reset from waste"
          tabIndex={0}
        >
          <span className="solitaire-pile__reset-icon">↻</span>
        </div>
      );
    }

    return (
      <div
        className="solitaire-pile solitaire-pile--stock"
        onClick={() => !disabled && onStockClick()}
        role="button"
        aria-label={`Stock pile, ${stock.length} cards remaining`}
        tabIndex={0}
      >
        <div className="solitaire-card solitaire-card--back">
          <div className="solitaire-card__back-pattern" />
          <span className="solitaire-pile__count">{stock.length}</span>
        </div>
      </div>
    );
  };

  const renderWaste = () => {
    const { waste } = gameState;

    if (waste.length === 0) {
      return (
        <div className="solitaire-pile solitaire-pile--waste solitaire-pile--empty" aria-label="Empty waste pile">
          <span className="solitaire-pile__placeholder" />
        </div>
      );
    }

    // Show up to 3 cards in draw-3 mode for visual stacking
    const visibleCount = gameState.drawMode === 3 ? Math.min(3, waste.length) : 1;
    const visibleCards = waste.slice(waste.length - visibleCount);

    return (
      <div className="solitaire-pile solitaire-pile--waste" aria-label={`Waste pile, ${waste.length} cards`}>
        <div className="solitaire-waste-stack">
          {visibleCards.map((card, i) => {
            const isTop = i === visibleCards.length - 1;
            const source: CardSource = { type: 'waste', index: 0, cardIndex: waste.length - visibleCount + i };
            return (
              <div
                key={card.id}
                className={`solitaire-waste-stack__card ${!isTop ? 'solitaire-waste-stack__card--offset' : ''}`}
                style={{ '--offset': i } as React.CSSProperties}
              >
                {isTop
                  ? renderCard(card, source)
                  : (
                    <div className={`solitaire-card solitaire-card--${getCardColor(card)}`} aria-hidden="true">
                      <div className="solitaire-card__corner solitaire-card__corner--top">
                        <span className="solitaire-card__rank">{RANK_LABELS[card.rank]}</span>
                        <span className="solitaire-card__suit">{SUIT_SYMBOLS[card.suit]}</span>
                      </div>
                    </div>
                  )
                }
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFoundation = (index: number) => {
    const foundation = gameState.foundations[index];
    const source: CardSource = { type: 'foundation', index, cardIndex: foundation.length - 1 };

    if (foundation.length === 0) {
      return (
        <div
          className="solitaire-pile solitaire-pile--foundation solitaire-pile--empty"
          onClick={() => !disabled && onEmptyClick({ type: 'foundation', index, cardIndex: 0 })}
          role="button"
          aria-label={`Empty foundation pile ${index + 1}`}
          tabIndex={0}
        >
          <span className="solitaire-pile__foundation-label">A</span>
        </div>
      );
    }

    const topCard = foundation[foundation.length - 1];
    return (
      <div className="solitaire-pile solitaire-pile--foundation" aria-label={`Foundation ${index + 1}, ${RANK_LABELS[topCard.rank]} of ${topCard.suit}`}>
        {renderCard(topCard, source)}
      </div>
    );
  };

  const renderTableauColumn = (colIndex: number) => {
    const column = gameState.tableau[colIndex];

    if (column.length === 0) {
      return (
        <div
          className="solitaire-tableau__column solitaire-tableau__column--empty"
          onClick={() => !disabled && onEmptyClick({ type: 'tableau', index: colIndex, cardIndex: 0 })}
          role="button"
          aria-label={`Empty tableau column ${colIndex + 1}`}
          tabIndex={0}
        >
          <span className="solitaire-pile__placeholder">K</span>
        </div>
      );
    }

    return (
      <div className="solitaire-tableau__column" aria-label={`Tableau column ${colIndex + 1}, ${column.length} cards`}>
        {column.map((card, cardIndex) => {
          const source: CardSource = { type: 'tableau', index: colIndex, cardIndex };
          const partOfSequence = isInSelectedSequence(colIndex, cardIndex);

          return (
            <div
              key={card.id}
              className="solitaire-tableau__card-wrapper"
              style={{ '--card-offset': cardIndex } as React.CSSProperties}
            >
              {renderCard(card, source, partOfSequence)}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="solitaire-board" role="region" aria-label="Solitaire game board">
      {/* Top row: Stock, Waste, gap, 4 Foundations */}
      <div className="solitaire-board__top-row">
        <div className="solitaire-board__stock-waste">
          {renderStock()}
          {renderWaste()}
        </div>
        <div className="solitaire-board__foundations">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="solitaire-board__foundation-slot">
              {renderFoundation(i)}
            </div>
          ))}
        </div>
      </div>

      {/* Tableau: 7 columns */}
      <div className="solitaire-board__tableau">
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="solitaire-board__tableau-slot">
            {renderTableauColumn(i)}
          </div>
        ))}
      </div>
    </div>
  );
};

export { SolitaireBoard };
export default SolitaireBoard;
